import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { formatMoney } from '@theme/money-formatting';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  /**
   * @returns {number}
   */
  get #protectionVariantId() {
    const id = parseInt(this.dataset.protectionVariantId ?? '', 10);
    return Number.isFinite(id) && id > 0 ? id : 0;
  }

  /**
   * Drawer count — matches header bubble (`cart.item_count`, shipping
   * protection included); 0 when only orphan shipping protection remains,
   * since the cleanup removes it right away.
   * @param {{ item_count?: number, items?: Array<{ variant_id: number, quantity: number, properties?: Record<string, string> }> }} cartData
   * @returns {number}
   */
  #getEffectiveItemCount(cartData) {
    if (window.ssCartCount) return window.ssCartCount.paidCount(cartData);
    if (!cartData?.items?.length) {
      return typeof cartData?.item_count === 'number' ? cartData.item_count : 0;
    }

    const protectionVariantId = this.#protectionVariantId;

    return cartData.items.reduce((sum, item) => {
      const isProtection =
        (protectionVariantId && item.variant_id === protectionVariantId) ||
        item.properties?._shipping_protection;
      const isGift =
        String(item.properties?._trio_gift || '') === '1' ||
        String(item.properties?._free_gift || '').toLowerCase() === 'yes';
      if (isProtection || isGift) return sum;
      return sum + (Number(item.quantity) || 0);
    }, 0);
  }

  /**
   * Remove shipping protection when it is the only remaining cart line.
   * @param {{ items?: Array<{ key: string, variant_id: number }> }} cartData
   * @param {Set<string>} sectionIds
   * @returns {Promise<{ cart: typeof cartData, removed: boolean, sections?: Record<string, string> }>}
   */
  async #removeOrphanProtectionIfNeeded(cartData, sectionIds) {
    const protectionVariantId = this.#protectionVariantId;
    if (!protectionVariantId || !cartData?.items?.length) {
      return { cart: cartData, removed: false };
    }

    const protectionLines = cartData.items.filter((item) => item.variant_id === protectionVariantId);
    const otherLines = cartData.items.filter((item) => item.variant_id !== protectionVariantId);
    if (protectionLines.length === 0 || otherLines.length > 0) {
      return { cart: cartData, removed: false };
    }

    const sections = Array.from(sectionIds).join(',');
    let lastResponse = cartData;

    for (let i = 0; i < protectionLines.length; i++) {
      const isLast = i === protectionLines.length - 1;
      const body = JSON.stringify({
        id: protectionLines[i].key,
        quantity: 0,
        ...(isLast
          ? {
              sections,
              sections_url: window.location.pathname,
            }
          : {}),
      });

      const response = await fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }));
      if (!response.ok) continue;
      lastResponse = JSON.parse(await response.text());
    }

    return {
      cart: lastResponse,
      removed: true,
      sections: lastResponse.sections,
    };
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    document.addEventListener('cart:protection-cleaned', this.#handleProtectionCleaned);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    document.removeEventListener('cart:protection-cleaned', this.#handleProtectionCleaned);
  }

  /**
   * @returns {boolean}
   */
  #isShowingEmptyState() {
    return this.querySelector('.custom-empty-cart-state') !== null;
  }

  /**
   * @param {boolean} [animate]
   */
  #showEmptyState(animate = false) {
    const template = document.getElementById('empty-cart-template');
    if (!(template instanceof HTMLTemplateElement) || this.#isShowingEmptyState()) return;

    const clone = document.importNode(template.content, true);
    const apply = () => {
      this.replaceChildren(clone);
    };

    if (animate && this.isDrawer) {
      startViewTransition(apply, ['empty-cart-drawer']);
    } else if (animate) {
      startViewTransition(apply, ['empty-cart-page']);
    } else {
      apply();
    }
  }

  /**
   * @param {CustomEvent<{ sectionId?: string, html?: string, itemCount?: number }>} event
   */
  #handleProtectionCleaned = (event) => {
    const { sectionId, html } = event.detail ?? {};
    if (!sectionId || sectionId !== this.sectionId || !html) return;
    if (this.#isShowingEmptyState()) return;

    const mode = this.isDrawer ? 'hydration' : 'full';
    morphSection(sectionId, html, { mode });
    this.#updateCartQuantitySelectorButtonStates();
  };

  /**
   * Sum of quantities across the given cart item rows, read from their
   * quantity inputs. Returns null when a row has no readable quantity.
   * @param {HTMLElement[]} rows
   * @returns {number | null}
   */
  #countRowQuantities(rows) {
    let total = 0;
    for (const row of rows) {
      const input = row.querySelector('input[name="updates[]"], input[data-cart-line]');
      if (!(input instanceof HTMLInputElement)) return null;
      const quantity = parseInt(input.value, 10);
      if (isNaN(quantity)) return null;
      total += quantity;
    }
    return total;
  }

  /**
   * Updates the header cart bubble immediately (in sync with the drawer's
   * optimistic UI) instead of waiting for the network round trip. The
   * authoritative count from the response confirms it afterwards.
   * @param {number | null} itemCount
   */
  #dispatchOptimisticItemCount(itemCount) {
    if (typeof itemCount !== 'number' || itemCount < 0) return;

    this.dispatchEvent(
      new CartUpdateEvent({}, this.sectionId, {
        itemCount,
        source: 'cart-items-component-optimistic',
        skipSectionRender: true,
      })
    );
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    this.#dispatchOptimisticItemCount(this.#countRowQuantities(this.refs.cartItemRows));
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // Treat as empty when no real product rows remain — the shipping
    // protection line (hidden or visible) never counts as content, since the
    // orphan cleanup removes it right away.
    const remainingRows = this.refs.cartItemRows.filter((row) => !rowsToRemove.includes(row));
    const isEmptyCart =
      remainingRows.length === 0 ||
      remainingRows.every(
        (row) =>
          row.classList.contains('custom-cart-card--hidden') ||
          row.classList.contains('cp__item--hidden') ||
          row.hasAttribute('data-protection-line')
      );

    // Bubble and drawer update in the same frame; the response confirms later.
    this.#dispatchOptimisticItemCount(isEmptyCart ? 0 : this.#countRowQuantities(remainingRows));

    if (isEmptyCart) {
      this.#showEmptyState(false);
      return;
    }

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const sectionsToUpdate = this.#getSectionsToUpdate();

    const cartItemRow = this.#getCartLineRow(line);
    const lineKey = cartItemRow instanceof HTMLElement ? cartItemRow.dataset.key : undefined;

    const body = JSON.stringify({
      ...(lineKey ? { id: lineKey } : { line }),
      quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(this.#getCartChangeUrl(), fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then(async (responseText) => {
        try {
          let parsedResponseText = JSON.parse(responseText);

          resetShimmer(this);

          if (parsedResponseText.errors) {
            this.#handleCartError(line, parsedResponseText);
            return;
          }

          const morphMode = this.#getMorphMode();
          const cleanup = await this.#removeOrphanProtectionIfNeeded(parsedResponseText, sectionsToUpdate);
          if (cleanup.removed) {
            parsedResponseText = cleanup.cart;
          }

          let sectionHtml =
            cleanup.sections?.[this.sectionId] ?? parsedResponseText.sections?.[this.sectionId];
          let newCartItemCount = this.#getEffectiveItemCount(parsedResponseText);

          if (sectionHtml) {
            const newSectionHTML = new DOMParser().parseFromString(sectionHtml, 'text/html');
            const countEls = [
              ...newSectionHTML.querySelectorAll(
                '.custom-cart-heading [ref="cartItemCount"], [ref="cartItemCount"]'
              ),
            ].filter((el) => !el.closest('template'));
            const newCartHiddenItemCount = countEls[0]?.textContent;
            if (newCartHiddenItemCount) {
              newCartItemCount = parseInt(newCartHiddenItemCount, 10);
            }
          }

          this.#updateQuantitySelectors(parsedResponseText);

          if (this.#isCartPage()) {
            this.#updateCartPageFromCart(parsedResponseText);
          } else if (newCartItemCount === 0) {
            if (this.#isShowingEmptyState()) {
              // Already cleared optimistically — avoid a second empty render / animation.
            } else if (document.getElementById('empty-cart-template') instanceof HTMLTemplateElement) {
              this.#showEmptyState(true);
            } else if (sectionHtml) {
              await this.#safeMorphSection(this.sectionId, sectionHtml, { mode: morphMode });
            } else {
              await sectionRenderer.renderSection(this.sectionId, { cache: false, mode: morphMode });
            }
          } else if (sectionHtml) {
            await this.#safeMorphSection(this.sectionId, sectionHtml, { mode: morphMode });
          } else {
            await sectionRenderer.renderSection(this.sectionId, { cache: false, mode: morphMode });
          }

          this.dispatchEvent(
            new CartUpdateEvent(parsedResponseText, this.sectionId, {
              itemCount: newCartItemCount,
              source: 'cart-items-component',
              sections: cleanup.sections ?? parsedResponseText.sections,
            })
          );

          if (!this.#isCartPage()) {
            this.#syncCpLinePrices(parsedResponseText);
          }

          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));

          this.#updateCartQuantitySelectorButtonStates();
        } catch (error) {
          console.error('[cart-items] quantity update failed', error);
        }
      })
      .catch((error) => {
        console.error('[cart-items] cart change request failed', error);
      })
      .finally(() => {
        resetShimmer(this);
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (quantityInput) {
      quantityInput.value = quantityInput.defaultValue;
    }

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (cartItemError instanceof HTMLElement && cartItemErrorContainer instanceof HTMLElement) {
      cartItemError.textContent = parsedResponseText.errors;
      cartItemErrorContainer.classList.remove('hidden');
      return;
    }

    console.error(parsedResponseText.errors);
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      const snapshot = this.#snapshotOfferUi();
      Promise.resolve(
        sectionRenderer.renderSection(this.sectionId, { cache: false, mode: this.#getMorphMode() })
      ).finally(() => this.#restoreOfferUi(snapshot));
      return;
    }
    if (event.target === this) return;

    if (event.detail.data?.skipSectionRender) return;

    const itemCount = event.detail.data?.itemCount;
    if (itemCount === 0 && this.#isShowingEmptyState()) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    const mode = this.#getMorphMode();
    const snapshot = this.#snapshotOfferUi();

    if (cartItemsHtml) {
      this.#safeMorphSection(this.sectionId, cartItemsHtml, mode).then(() => {
        this.#syncCpLinePrices(event.detail.resource);
        this.#updateCartQuantitySelectorButtonStates();
        this.#restoreOfferUi(snapshot);
      });
    } else if (event.detail.resource) {
      this.#syncCpLinePrices(event.detail.resource);
      Promise.resolve(sectionRenderer.renderSection(this.sectionId, { cache: false, mode })).finally(() =>
        this.#restoreOfferUi(snapshot)
      );
    } else {
      Promise.resolve(sectionRenderer.renderSection(this.sectionId, { cache: false, mode })).finally(() =>
        this.#restoreOfferUi(snapshot)
      );
    }

    this.#enableCartItems();
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.#getLiveRoot().classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    document
      .querySelectorAll(`cart-items-component[data-section-id="${this.sectionId}"]`)
      .forEach((node) => node.classList.remove('cart-items-disabled'));
  }

  /**
   * @returns {string}
   */
  #getCartChangeUrl() {
    let url = Theme?.routes?.cart_change_url ?? '/cart/change';
    if (!url.endsWith('.js')) {
      url = `${url.replace(/\/$/, '')}.js`;
    }
    return url;
  }

  /**
   * @param {number} line
   * @returns {HTMLElement | undefined}
   */
  #getCartLineRow(line) {
    const root = this.#getLiveRoot();
    const input = root.querySelector(`.cp__qty-input[data-cart-line="${line}"]`);
    const rowFromInput = input?.closest('.cp__item');
    if (rowFromInput instanceof HTMLElement) return rowFromInput;
    const rowFromRefs = this.refs.cartItemRows?.[line - 1];
    return rowFromRefs instanceof HTMLElement ? rowFromRefs : undefined;
  }

  /**
   * @returns {HTMLElement}
   */
  #getLiveRoot() {
    return (
      document.querySelector(`cart-items-component[data-section-id="${this.sectionId}"]`) || this
    );
  }

  /**
   * @returns {boolean}
   */
  #isCartPage() {
    return !this.isDrawer && Boolean(this.#getLiveRoot().querySelector('.cp'));
  }

  /**
   * @returns {string}
   */
  #getMoneyFormat() {
    const root = this.#getLiveRoot();
    const fromDataset = root.dataset.moneyFormat || this.dataset.moneyFormat || '';
    if (fromDataset) return fromDataset;
    if (typeof window !== 'undefined' && window.__cpMoneyFormat) {
      return window.__cpMoneyFormat;
    }
    return '';
  }

  /**
   * @param {string} key
   * @returns {HTMLElement | null}
   */
  #findCartPageRow(key, variantId) {
    const root = this.#getLiveRoot();

    if (key) {
      const byKey = root.querySelector(`.cp__item[data-key="${CSS.escape(key)}"]`);
      if (byKey) return byKey;
    }

    if (variantId) {
      const byVariant = root.querySelector(`.cp__item[data-variant-id="${variantId}"]`);
      if (byVariant) return byVariant;
    }

    return null;
  }

  /**
   * @param {{ quantity: number, final_line_price?: number, line_price?: number, final_price?: number, original_line_price?: number, original_price?: number, compare_at_price?: number, price?: number }} item
   * @returns {number | null}
   */
  #getLinePriceCents(item) {
    if (Number.isFinite(item.final_line_price)) return item.final_line_price;
    if (Number.isFinite(item.line_price)) return item.line_price;
    if (Number.isFinite(item.final_price) && Number.isFinite(item.quantity)) {
      return item.final_price * item.quantity;
    }
    if (Number.isFinite(item.price) && Number.isFinite(item.quantity)) {
      return item.price * item.quantity;
    }
    return null;
  }

  /**
   * @param {{ quantity: number, original_line_price?: number, original_price?: number, compare_at_price?: number, price?: number, final_price?: number }} item
   * @param {number | null} linePriceCents
   * @returns {number | null}
   */
  #getCompareLineCents(item, linePriceCents) {
    if (
      Number.isFinite(item.original_line_price) &&
      linePriceCents !== null &&
      item.original_line_price !== linePriceCents
    ) {
      return item.original_line_price;
    }

    if (
      Number.isFinite(item.original_price) &&
      Number.isFinite(item.quantity) &&
      linePriceCents !== null &&
      item.original_price * item.quantity !== linePriceCents
    ) {
      return item.original_price * item.quantity;
    }

    if (
      Number.isFinite(item.compare_at_price) &&
      Number.isFinite(item.price) &&
      item.compare_at_price > item.price &&
      Number.isFinite(item.quantity)
    ) {
      return item.compare_at_price * item.quantity;
    }

    return null;
  }

  /**
   * Update cart page prices and summary directly from cart/change JSON (no section morph).
   * @param {{ items?: Array<{ key: string, variant_id: number, quantity: number, final_line_price?: number, line_price?: number, final_price?: number, original_line_price?: number, original_price?: number, compare_at_price?: number, price?: number }>, total_price?: number, original_total_price?: number, total_discount?: number, item_count?: number, currency?: string }} cart
   */
  #updateCartPageFromCart(cart) {
    if (typeof window.cpSyncCartPrices === 'function') {
      window.cpSyncCartPrices(cart);
      return;
    }

    const moneyFormat = this.#getMoneyFormat();
    if (!moneyFormat) {
      console.warn('[cart-items] missing money format — cannot sync cart page prices');
      return;
    }
    if (!cart?.items?.length) return;

    const root = this.#getLiveRoot();
    const currency = cart.currency ?? 'USD';

    for (const item of cart.items) {
      const row = this.#findCartPageRow(item.key, String(item.variant_id));
      if (!row || row.classList.contains('cp__item--hidden')) continue;

      const qtyInput = row.querySelector('.cp__qty-input');
      if (qtyInput instanceof HTMLInputElement && Number.isFinite(item.quantity)) {
        qtyInput.value = String(item.quantity);
      }

      const pricesEl = row.querySelector('.cp__item-prices');
      if (!pricesEl) continue;

      const linePriceCents = this.#getLinePriceCents(item);
      const priceEl = pricesEl.querySelector('[data-line-price]');
      if (priceEl && linePriceCents !== null) {
        priceEl.textContent = formatMoney(linePriceCents, moneyFormat, currency);
      }

      const compareCents = this.#getCompareLineCents(item, linePriceCents);
      let compareEl = pricesEl.querySelector('[data-line-compare]');
      if (compareCents !== null) {
        if (!compareEl) {
          compareEl = document.createElement('span');
          compareEl.className = 'cp__item-compare';
          compareEl.setAttribute('data-line-compare', '');
          priceEl?.before(compareEl);
        }
        compareEl.textContent = formatMoney(compareCents, moneyFormat, currency);
      } else if (compareEl) {
        compareEl.remove();
      }
    }

    const originalSubtotalEl = root.querySelector('[data-cart-original-subtotal]');
    if (originalSubtotalEl && Number.isFinite(cart.original_total_price)) {
      originalSubtotalEl.textContent = formatMoney(cart.original_total_price, moneyFormat, currency);
    }

    const savingsEl = root.querySelector('[data-cart-savings]');
    if (savingsEl && Number.isFinite(cart.total_discount) && cart.total_discount > 0) {
      savingsEl.textContent = `-${formatMoney(cart.total_discount, moneyFormat, currency)}`;
    }

    const totalEl = root.querySelector('[data-cart-subtotal]');
    if (totalEl && Number.isFinite(cart.total_price)) {
      totalEl.textContent = formatMoney(cart.total_price, moneyFormat, currency);
    }

    const ritualCountEl = root.querySelector('[data-cp-ritual-count]');
    if (ritualCountEl) {
      if (window.ssCartCount) {
        ritualCountEl.textContent = window.ssCartCount.ritualSummary(
          window.ssCartCount.paidCount(cart),
          window.ssCartCount.giftCount(cart),
          { products: 'products', gifts: 'gifts', items: root.dataset.cpItemsSuffix || 'items' }
        );
      } else if (typeof cart.item_count === 'number') {
        const count = this.#getEffectiveItemCount(cart);
        const suffix = root.dataset.cpItemsSuffix || 'items';
        ritualCountEl.textContent = `${count} ${suffix}`;
      }
    }

    const threshold = parseInt(root.dataset.cpShippingThreshold || '0', 10);
    const shipFill = root.querySelector('.cp__ship-fill');
    if (shipFill && threshold > 0 && Number.isFinite(cart.total_price)) {
      const percent = Math.min(100, (cart.total_price / threshold) * 100);
      shipFill.style.width = `${percent}%`;
    }

    const shipWrap = root.querySelector('.cp__ship');
    if (shipWrap && threshold > 0 && Number.isFinite(cart.total_price)) {
      shipWrap.classList.toggle('cp__ship--unlocked', cart.total_price >= threshold);
    }
  }

  /**
   * @returns {Set<string>}
   */
  #getSectionsToUpdate() {
    const sectionsToUpdate = new Set([this.sectionId]);

    if (this.isDrawer) {
      document.querySelectorAll('cart-items-component').forEach((item) => {
        if (item instanceof HTMLElement && item.dataset.sectionId) {
          sectionsToUpdate.add(item.dataset.sectionId);
        }
      });
    }

    return sectionsToUpdate;
  }

  /**
   * @returns {'hydration' | 'full'}
   */
  #getMorphMode() {
    if (this.isDrawer) return 'hydration';
    if (this.querySelector('[data-hydration-key]')) return 'hydration';
    return 'full';
  }

  /**
   * @param {string} sectionId
   * @param {string} sectionHtml
   * @param {'hydration' | 'full'} mode
   */
  #snapshotOfferUi() {
    if (!this.#isCartPage() || typeof window.cpSnapshotOfferUi !== 'function') return null;
    try {
      return window.cpSnapshotOfferUi(this.#getLiveRoot());
    } catch (error) {
      return null;
    }
  }

  #restoreOfferUi(snapshot) {
    if (!snapshot || typeof window.cpRestoreOfferUi !== 'function') return;
    try {
      window.cpRestoreOfferUi(this.#getLiveRoot(), snapshot);
    } catch (error) {}
  }

  async #safeMorphSection(sectionId, sectionHtml, mode) {
    const snapshot = this.#snapshotOfferUi();
    try {
      await morphSection(sectionId, sectionHtml, { mode });
    } catch (error) {
      console.warn('[cart-items] morph failed, replacing section node', error);
      this.#replaceSectionNode(sectionId, sectionHtml);
    }
    this.#restoreOfferUi(snapshot);
  }

  /**
   * @param {string} sectionId
   * @param {string} sectionHtml
   */
  #replaceSectionNode(sectionId, sectionHtml) {
    const doc = new DOMParser().parseFromString(sectionHtml, 'text/html');
    const liveNode = document.getElementById(`shopify-section-${sectionId}`);
    const nextNode =
      doc.getElementById(`shopify-section-${sectionId}`) || doc.querySelector('[id^="shopify-section-"]');

    if (liveNode && nextNode) {
      liveNode.replaceWith(nextNode);
    }
  }

  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(
        `quantity-selector-component[data-variant-id="${variantId}"], cart-quantity-selector-component[data-variant-id="${variantId}"]`
      );

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        const row = selector.closest('.cp__item, .custom-cart-card');
        const inputKey = input.getAttribute('data-line-key') || row?.getAttribute('data-key') || '';
        if (inputKey && item.key && inputKey !== item.key) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());
        if (input instanceof HTMLInputElement && !input.matches(':focus')) {
          input.value = String(item.quantity);
        }

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Sync cart page line totals and order summary from cart/change JSON.
   * @param {{ items?: Array<{ key: string, quantity: number, price: number, final_price: number, final_line_price: number, original_line_price: number, compare_at_price?: number }>, total_price?: number, original_total_price?: number, currency?: string }} updatedCart
   */
  #syncCpLinePrices(updatedCart) {
    try {
      if (this.#isCartPage()) {
        this.#updateCartPageFromCart(updatedCart);
        return;
      }
    } catch (error) {
      console.warn('[cart-items] price sync failed', error);
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
